import { Link } from "react-router-dom";
import { Accessibility, Mail, FileCheck, Keyboard, Eye, Smartphone } from "lucide-react";

export default function AccessibilityPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">OUR COMMITMENT</p>
      <h1 className="cm-heading text-4xl sm:text-5xl lg:text-6xl mt-3">
        Accessibility <span className="text-copper">Statement</span>
      </h1>
      <p className="mt-6 text-gray-300 leading-relaxed">
        HairMaxxing Pro is committed to ensuring digital accessibility for people with
        disabilities. We are continually improving the user experience for everyone and
        applying the relevant accessibility standards.
      </p>

      <section aria-labelledby="standard" className="mt-12">
        <h2 id="standard" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <FileCheck className="w-5 h-5 text-copper" aria-hidden="true" />
          Conformance status
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          The Web Content Accessibility Guidelines (WCAG) defines requirements for designers
          and developers to improve accessibility for people with disabilities. It defines
          three levels of conformance: Level A, Level AA, and Level AAA. HairMaxxing Pro
          is <strong className="text-platinum">partially conformant with WCAG 2.1 level AA</strong>.
          Partially conformant means that some parts of the content do not fully conform to
          the accessibility standard.
        </p>
      </section>

      <section aria-labelledby="features" className="mt-10">
        <h2 id="features" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Accessibility className="w-5 h-5 text-copper" aria-hidden="true" />
          Accessibility features
        </h2>
        <ul className="mt-4 space-y-3 text-gray-300 leading-relaxed list-disc list-inside marker:text-copper">
          <li>Semantic HTML landmarks (header, nav, main, footer) for assistive technology.</li>
          <li>Skip-to-content link visible on keyboard focus.</li>
          <li>Visible focus indicators on every interactive element.</li>
          <li>Keyboard-only navigation supported throughout the app.</li>
          <li>Mobile menu uses {`role="dialog"`}, <code>aria-modal</code>, and closes on Escape.</li>
          <li>All images include descriptive alt text; decorative icons are <code>aria-hidden</code>.</li>
          <li>Form fields are associated with labels via <code>htmlFor</code>.</li>
          <li>Color contrast meets WCAG AA (4.5:1 for body text, 3:1 for large text).</li>
          <li>Respects <code>prefers-reduced-motion</code> — animations are minimized for users who request it.</li>
          <li>Text resizable up to 200% without loss of content or functionality.</li>
        </ul>
      </section>

      <section aria-labelledby="tested" className="mt-10">
        <h2 id="tested" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Eye className="w-5 h-5 text-copper" aria-hidden="true" />
          Tested compatibility
        </h2>
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-300">
          <li className="cm-card p-4 flex items-start gap-3">
            <Keyboard className="w-4 h-4 text-copper mt-1" aria-hidden="true" />
            <div>
              <p className="cm-label text-[10px]">Keyboard</p>
              <p className="text-sm mt-1">Tab, Shift+Tab, Enter, Escape</p>
            </div>
          </li>
          <li className="cm-card p-4 flex items-start gap-3">
            <Smartphone className="w-4 h-4 text-copper mt-1" aria-hidden="true" />
            <div>
              <p className="cm-label text-[10px]">Screen readers</p>
              <p className="text-sm mt-1">VoiceOver (iOS / macOS), NVDA, JAWS</p>
            </div>
          </li>
        </ul>
      </section>

      <section aria-labelledby="known" className="mt-10">
        <h2 id="known" className="cm-heading text-2xl text-platinum">
          Known limitations
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          Despite our best efforts, some legacy content and third-party integrations
          (Stripe Checkout, Google Sign-In) may have accessibility issues outside our
          direct control. If you encounter a barrier, please contact us &mdash; we&rsquo;ll work
          with the third party or provide an alternative.
        </p>
      </section>

      <section aria-labelledby="feedback" className="mt-10 cm-card p-6 border-copper/40">
        <h2 id="feedback" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Mail className="w-5 h-5 text-copper" aria-hidden="true" />
          Feedback &amp; assistance
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          We welcome your feedback on the accessibility of HairMaxxing Pro.
          If you encounter accessibility barriers, please let us know — we typically
          respond within 2 business days.
        </p>
        <a
          href="mailto:Support@hairmaxxingpro.com?subject=Accessibility%20Feedback"
          className="cm-btn-primary inline-flex mt-5"
          data-testid="accessibility-contact-link"
        >
          Email Support@hairmaxxingpro.com
        </a>
      </section>

      <p className="mt-12 text-xs text-gray-500">
        This statement was last updated on{" "}
        <time dateTime="2026-02-01">February 2026</time>.
      </p>

      <div className="mt-8">
        <Link
          to="/"
          className="cm-btn-secondary inline-flex"
          data-testid="accessibility-back-home"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}
